import React from "react";

interface ResumeCardProps {
  label: string;
  onContinue: () => void;
  onForget: () => void;
  loading?: boolean;
}

/**
 * "Continue as …" resume shortcut, shown beneath the email form when a valid
 * SSO session is still resumable (ZC-5707). Rendered as a sibling alternative to
 * the primary email Continue button — an "or" divider separates the two, and the
 * card itself is a full-width secondary action so it reads as a one-click
 * shortcut, not the main path. The × forgets the saved login (switch user).
 */
export default function ResumeCard({
  label,
  onContinue,
  onForget,
  loading = false,
}: ResumeCardProps) {
  const initial = label.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="resume-card mt-4">
      <div className="resume-card__divider" role="separator" aria-hidden="true">
        <span>or</span>
      </div>

      <div className="resume-card__shell d-flex align-items-stretch">
        <button
          type="button"
          className="resume-card__continue d-flex align-items-center gap-3 flex-grow-1 text-start"
          onClick={onContinue}
          disabled={loading}
          aria-label={`Continue as ${label}`}
        >
          <span className="resume-card__avatar" aria-hidden="true">
            {initial}
          </span>
          <span className="resume-card__id overflow-hidden">
            <span className="resume-card__eyebrow">Continue as</span>
            <span className="resume-card__email text-truncate d-block">
              {label}
            </span>
          </span>
          <span className="resume-card__chevron ms-auto" aria-hidden="true">
            ›
          </span>
        </button>

        <button
          type="button"
          className="resume-card__forget"
          onClick={onForget}
          aria-label={`Forget ${label}`}
          title="Not you? Forget this login"
          disabled={loading}
        >
          &times;
        </button>
      </div>
    </div>
  );
}
